import * as Popover from '@radix-ui/react-popover';

import { Calendar16 } from 'ui/icons/aurora/16px/Calendar16';
import { ALIGNMENT } from 'ui/selects/constants';
import { HORIZONTAL_ALIGNMENT, VERTICAL_ALIGNMENT } from 'ui/utility/types';
import { DATE_PICKER_PORTAL_ID, SingleDatePicker } from 'ui/datePicker';
import { useDropdownPopoverAlignment } from 'ui/utility/hooks';
import { DateAndCalendarWrapper } from 'ui/datePicker/DatePicker.styles';
import { RelativeDateValues } from 'ui/datePicker/singleDatePickerComponents';
import { noop } from 'utils/noop';
import { guessCurrentTimeZone } from 'utils/datetime';

import { ConditionDatePickerTriggerButton } from './ConditionDatePickerTrigger';
import { isEmptyDate } from './utils';
import { ConditionSingleValueDateProps } from '../types';
import { useConditionDateState } from './hooks/useConditionDateState';

export const DATA_TEST = {
  content: 'condition-date-picker-content',
};

const RELATIVE_DATE_MENU_WITHOUT_HANDLEBARS = [
  RelativeDateValues[0],
  RelativeDateValues[1],
  { Rounding: RelativeDateValues[2].Rounding },
];

export const ConditionDatePicker = ({
  value = null,
  defaultTimeZone = guessCurrentTimeZone(),
  isDisabled = false,
  hasError = false,
  isReadOnly = false,
  isOpenByDefault = false,
  onChange = noop,
  placeholder = '',
  ...props
}: ConditionSingleValueDateProps) => {
  const {
    date,
    timeZone,
    isRelativeDate,
    triggerDate,
    isEditing,
    defaultDate,
    onTimeZoneChange,
    onDateChange,
    onRelativeDateToggle,
    onBlurSave,
  } = useConditionDateState({ value, defaultTimeZone, onChange, isOpenByDefault });

  const { popoverVerticalAlignment, popoverHorizontalAlignment } = useDropdownPopoverAlignment(
    ALIGNMENT.LEFT as HORIZONTAL_ALIGNMENT,
    ALIGNMENT.DOWN as VERTICAL_ALIGNMENT,
  );

  return (
    <Popover.Root open={isEditing} onOpenChange={onBlurSave}>
      <DateAndCalendarWrapper>
        <Popover.Trigger asChild>
          <ConditionDatePickerTriggerButton
            {...props}
            hasError={hasError}
            isEmpty={isEmptyDate(triggerDate)}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
            value={triggerDate}
            onChange={onChange}
            placeholder={placeholder}
            DefaultIcon={<Calendar16 isColorInherited />}
          />
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align={popoverHorizontalAlignment}
            avoidCollisions={false}
            collisionPadding={{ top: 80 }}
            data-portal-id={DATE_PICKER_PORTAL_ID}
            data-test={DATA_TEST.content}
            hideWhenDetached
            side={popoverVerticalAlignment}
            sideOffset={4}
          >
            <SingleDatePicker
              date-picker-id={DATE_PICKER_PORTAL_ID}
              date={date ?? defaultDate}
              defaultTimeZone={timeZone}
              hasError={hasError}
              hideTimeZonePicker
              isRelativeDate={isRelativeDate}
              onDateChange={onDateChange}
              onRelativeDateToggle={onRelativeDateToggle}
              onTimeZoneChange={onTimeZoneChange}
              showTime
              useRelativeDate
              relativeDateMenuValues={RELATIVE_DATE_MENU_WITHOUT_HANDLEBARS}
            />
          </Popover.Content>
        </Popover.Portal>
      </DateAndCalendarWrapper>
    </Popover.Root>
  );
};
